import {
  useDashboardCard,
  useDashboardStructure,
} from '@/features/dashboard/api/queries'
import {
  MARKET_FILTER,
  TIMEFRAME_FILTER,
  type CardStub,
} from '@/features/dashboard/api/schemas'
import type { CardFilters } from '@/features/dashboard/api/client'
import {
  deepDiveTakesMarkets,
  homeCardOf,
} from '@/features/dashboard/model/sections'
import { useDashboardFilters, useSectionSelection } from './useDashboardFilters'
import { REGULATORY_FILTERS, useRegulatoryFilters } from './regulatoryFilters'

const ALL_TIME = 0

/**
 * A card drawn in a deep dive. It is fetched for that deep dive, so its
 * settings there apply, and sliced by the filters its stub lists: the markets,
 * the regulatory selections and, for a time series, the timeframe and range.
 * The timeframe and range are the page's, shared with the home section of the
 * deep dive's headline card (`homeCardOf`), so every card follows the bar
 * under the page header and the home page. Without a home card, or for a card
 * not listing the timeframe, none is sent.
 */
export const useDeepDiveCard = (stub: CardStub, deepDiveId: string) => {
  const { data: dashboard } = useDashboardStructure()
  const { markets, clearMarkets } = useDashboardFilters()
  const { selection } = useRegulatoryFilters()
  const homeSectionId = dashboard
    ? homeCardOf(dashboard.sections, deepDiveId)?.section.id
    : undefined
  const { selection: timeSelection } = useSectionSelection(homeSectionId)

  const takesTimeframe =
    (stub.filters?.includes(TIMEFRAME_FILTER) ?? false) &&
    timeSelection !== undefined
  // The title tells apart the stubs of a deep dive sharing one card id.
  const filters: CardFilters = { deepDive: deepDiveId, title: stub.name }
  if (takesTimeframe) {
    filters.timeframe = timeSelection.timeframe
    // As at home, a positive range is applied to the card client-side (see
    // `periodWindow`), so all time is asked for where it can be.
    const canAskAll = dashboard?.filters?.timeframes
      ?.find(option => option.id === timeSelection.timeframe)
      ?.periods.options.some(range => range.value === ALL_TIME)
    filters.periods =
      timeSelection.periods > 0 && canAskAll ? ALL_TIME : timeSelection.periods
  }
  // Only the Customers deep dives take the market filter.
  const takesMarkets = dashboard
    ? deepDiveTakesMarkets(dashboard.sections, deepDiveId)
    : false
  if (takesMarkets && stub.filters?.includes(MARKET_FILTER)) {
    filters.markets = markets
  }
  for (const { filter, param } of REGULATORY_FILTERS) {
    if (stub.filters?.includes(filter) && selection[param].length > 0) {
      filters[param] = selection[param]
    }
  }

  return {
    ...useDashboardCard(stub.cardId, filters),
    clearMarkets,
    timeframe: takesTimeframe ? timeSelection.timeframe : undefined,
    months: takesTimeframe ? timeSelection.periods : undefined,
  }
}
